import * as React from 'react';
import type { Site } from '@klareospace/pharos-lib-types';
import { cn } from '@klareospace/pharos-lib-web-utils';
import { useTranslation } from 'react-i18next';
import { useMasterContext } from '&/master';
import { SITE_TYPE_ICON_MAP } from '@klareospace/pharos-lib-web-utils';
import { SITE_TYPE_LABEL_KEY } from '../../utils/site-icon-map';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SiteRowProps {
  onClose: () => void;
  site: Site;
  spaceId: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SiteRow({ onClose, site, spaceId }: SiteRowProps): React.JSX.Element {
  const { selectSite, siteId: currentSiteId } = useMasterContext();
  const { t } = useTranslation('common');
  const SiteIcon = SITE_TYPE_ICON_MAP[site.type];
  const isSelected = site.id === currentSiteId;
  const typeLabel = t(SITE_TYPE_LABEL_KEY[site.type]);

  const handleSelectSite = (): void => {
    selectSite(site.id, spaceId);
    onClose();
  };

  return (
    <button
      className={cn(
        'flex items-center gap-2 px-2.5 py-2 w-full rounded-md transition-colors text-left group/site-row',
        isSelected ? 'bg-accent' : 'hover:bg-muted',
      )}
      onClick={handleSelectSite}
      type="button"
    >
      <SiteIcon className="w-3.5 h-3.5 shrink-0" style={{ color: site.color }} />
      <span className="flex-1 text-xs truncate">{site.name}</span>

      {/* Type label — hidden on hover for non-selected */}
      <span className={cn('text-[11px] text-muted-foreground', !isSelected && 'group-hover/site-row:hidden')}>
        {typeLabel}
      </span>

      {/* Select text — shown on hover for non-selected */}
      {!isSelected && (
        <span className="text-[11px] text-primary font-medium hidden group-hover/site-row:inline">
          {t('mainSelector.select')}
        </span>
      )}
    </button>
  );
}
